import { createTheme } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';

import type { ThemeColors, ThemeConfig } from '@/infra/theme/theme-config.js';
import { DARK_THEME_COLORS, LIGHT_THEME_COLORS } from '@/infra/theme/theme-defaults.js';
import { BRAND_NAVY, BRAND_ORANGE } from '@/shared/constants/brand.js';

declare module '@mui/material/styles' {
    interface Palette {
        brand: {
            navy: string;
            orange: string;
        };
    }

    interface PaletteOptions {
        brand?: {
            navy: string;
            orange: string;
        };
    }
}

function buildHeadingTypography(fontFamily: string) {
    const heading = { fontFamily, fontWeight: 700 } as const;
    return { h1: heading, h2: heading, h3: heading, h4: heading, h5: heading, h6: heading };
}

export function resolveEffectiveDarkMode(config: ThemeConfig, systemMode: 'light' | 'dark' = 'light'): 'light' | 'dark' {
    if (config.darkMode === 'system') {
        return systemMode;
    }
    return config.darkMode;
}

export function resolveEffectiveThemeConfig(config: ThemeConfig, systemMode: 'light' | 'dark' = 'light', baseColors?: ThemeColors): ThemeConfig {
    const effectiveDarkMode = resolveEffectiveDarkMode(config, systemMode);
    const paletteColors = effectiveDarkMode === 'dark' ? DARK_THEME_COLORS : (baseColors ?? LIGHT_THEME_COLORS);
    return {
        ...config,
        darkMode: effectiveDarkMode,
        colors: {
            ...paletteColors,
            primary: config.colors.primary,
            secondary: config.colors.secondary,
            primaryLight: config.colors.primaryLight,
            primaryDark: config.colors.primaryDark,
            secondaryLight: config.colors.secondaryLight,
            secondaryDark: config.colors.secondaryDark
        }
    };
}

export function buildMuiTheme(config: ThemeConfig): Theme {
    const isDark = config.darkMode === 'dark';

    return createTheme({
        palette: {
            mode: isDark ? 'dark' : 'light',
            primary: { main: config.colors.primary, light: config.colors.primaryLight, dark: config.colors.primaryDark },
            secondary: { main: config.colors.secondary, light: config.colors.secondaryLight, dark: config.colors.secondaryDark },
            error: { main: config.colors.error },
            warning: { main: config.colors.warning },
            success: { main: config.colors.success },
            info: { main: config.colors.info },
            background: { default: config.colors.background, paper: config.colors.surface },
            text: { primary: config.colors.textPrimary, secondary: config.colors.textSecondary },
            divider: config.colors.border,
            brand: { navy: BRAND_NAVY, orange: BRAND_ORANGE }
        },
        typography: {
            fontFamily: config.typography.fontFamily,
            fontSize: parseInt(config.typography.baseFontSize) || 14,
            ...buildHeadingTypography(config.typography.headingFontFamily),
            subtitle1: { fontFamily: config.typography.headingFontFamily, fontWeight: 600 },
            subtitle2: { fontFamily: config.typography.headingFontFamily, fontWeight: 600 }
        },
        shape: { borderRadius: parseInt(config.layout.borderRadius) || 8 }
    });
}

export function themeToCustomProperties(config: ThemeConfig): string {
    const lines = [
        ':root {',
        ...Object.entries(config.colors).map(([key, value]) => `  --color-${key.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${value};`),
        `  --font-family: ${config.typography.fontFamily};`,
        `  --border-radius: ${config.layout.borderRadius};`,
        `  --header-height: ${config.layout.headerHeight};`,
        `  --sidebar-width: ${config.layout.sidebarWidth};`,
        '}'
    ];
    return lines.join('\n');
}
